import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLanguage, t } from '../utils/i18n';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [alertInfo, setAlertInfo] = useState(null);
  const { language } = useLanguage();

  // Load cart from localStorage on mount
  useEffect(() => {
    try {
      const savedCart = localStorage.getItem('akasha-cart');
      if (savedCart) {
        setCartItems(JSON.parse(savedCart));
      }
    } catch (error) {
      console.error('Error loading cart from localStorage:', error);
    }
  }, []);

  // Save cart to localStorage whenever cartItems change
  useEffect(() => {
    try {
      localStorage.setItem('akasha-cart', JSON.stringify(cartItems));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [cartItems]);

  // Clear alert after 3 seconds
  useEffect(() => {
    if (alertInfo) {
      const timer = setTimeout(() => {
        setAlertInfo(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [alertInfo]);

  const addToCart = (product, quantity = 1) => {
    // Check if product has stock
    if (product.stock <= 0) {
      setAlertInfo({
        type: 'error',
        message: t('product.outOfStock', language.code)
      });
      return;
    }

    let itemAddedName = product.name;
    let itemAddedQuantity = quantity;

    setCartItems(prevItems => {
      const existingItem = prevItems.find(item => item.id === product.id);
      if (existingItem) {
        const newQuantity = existingItem.quantity + quantity;
        // Check if adding would exceed stock
        if (newQuantity > product.stock) {
          setAlertInfo({
            type: 'error',
            message: t('product.stockLimit', language.code, { max: product.stock })
          });
          return prevItems; // Don't add if would exceed stock limit
        }
        itemAddedQuantity = newQuantity;
        return prevItems.map(item => 
          item.id === product.id 
            ? { ...item, quantity: newQuantity }
            : item
        );
      } else {
        // Check if initial quantity exceeds stock
        if (quantity > product.stock) {
          setAlertInfo({
            type: 'error',
            message: t('product.stockLimit', language.code, { max: product.stock })
          });
          return prevItems;
        }
        return [...prevItems, { ...product, quantity }];
      }
    });

    setShowCart(true);
    setAlertInfo({
      type: 'success',
      message: t('product.addToCart.alert', language.code, { 
        quantity: itemAddedQuantity, 
        name: itemAddedName 
      })
    });
  };

  const removeFromCart = (productId) => {
    setCartItems(prevItems => prevItems.filter(item => item.id !== productId));
  };

  const updateCartItemQuantity = (productId, newQuantity) => {
    setCartItems(prevItems => prevItems.map(item => {
      if (item.id === productId) {
        // Ensure quantity doesn't exceed stock and is at least 1
        const limitedQuantity = Math.min(Math.max(1, newQuantity), item.stock || 999);
        return { ...item, quantity: limitedQuantity };
      }
      return item;
    }));
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartItemCount = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const value = {
    cartItems,
    showCart,
    setShowCart,
    alertInfo,
    setAlertInfo,
    addToCart,
    removeFromCart,
    updateCartItemQuantity,
    clearCart,
    getCartTotal,
    getCartItemCount
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext; 